import { getSizeGuideData } from "../api/sizeGuide";

const sizeGuideCache = {};

export async function getSizeGuide(sizeGuideId) {
  if (!sizeGuideCache[sizeGuideId]) {
    sizeGuideCache[sizeGuideId] = getSizeGuideData(sizeGuideId).catch(
      (error) => {
        delete sizeGuideCache[sizeGuideId];
        throw error;
      }
    );
  }

  return sizeGuideCache[sizeGuideId];
}

export const getMeasurementForSize = (
  sizeGuideData,
  brandSizeText,
  measurementKey,
  storeCode
) => {
  if (!sizeGuideData || !brandSizeText) return null;

  const label = brandSizeText.trim();

  if (label.includes("W")) return null;
  if (/\bL\s*\d+\b/i.test(label)) return null;
  if (/(chest|waist|bust)/i.test(label)) return null;
  if (/-\s*\D*\d/.test(label)) return null;

  const isSimpleAlpha =
    /^(XXS|XS|S|M|L|XL|XXL|XXXL|2XL|3XL|4XL|5XL|6XL|7XL|8XL|9XL)$/i.test(label);

  const isUkUsAuNumeric = /^(UK|US|AU)\s*\d+(\s*[-–/]\s*\d+)?(\s*.*)?$/i.test(
    label
  );

  if (!isSimpleAlpha && !isUkUsAuNumeric) return null;

  const { sizes } = sizeGuideData;
  if (!Array.isArray(sizes)) return null;

  const schemaMap = {
    AU: ["AU"],
    US: ["US"],
    ROW: ["EU", "AU"], // ROW tries EU first, then AU
  };
  const schemasToTry = schemaMap[storeCode?.toUpperCase?.()] || [
    storeCode?.toUpperCase?.(),
  ];

  let sizeData = null;
  let usedSchema = null;
  for (const targetSchema of schemasToTry) {
    sizeData = sizes.find((s) => {
      const conversions = s.conversions || [];
      const conversionData = conversions.find((c) => c.schema === targetSchema);
      return (
        conversionData && conversionData.internationalConversionText === label
      );
    });
    if (sizeData) {
      usedSchema = targetSchema;
      break; // Found a match, stop trying other schemas
    }
  }

  if (!sizeData) {
    sizeData = sizes.find(
      (s) =>
        typeof s.brandSizeText === "string" && s.brandSizeText.trim() === label
    );
  }

  if (!sizeData) return null;

  const { dimensions } = sizeData;
  if (!Array.isArray(dimensions)) return null;

  const dimensionData = dimensions.find(
    (d) => d.dimensionText === measurementKey
  );
  if (!dimensionData) return null;

  const { measurements } = dimensionData;
  if (!Array.isArray(measurements)) return null;

  // If AU schema was used, display in CM; otherwise IN
  const measurementUnit = usedSchema === "AU" ? "CM" : "IN";
  const targetMeasurement = measurements.find(
    (m) => m.unit === measurementUnit
  );
  if (!targetMeasurement) return null;

  const rawValue = targetMeasurement.value;
  const roundedValue = roundDecimalsInLabel(rawValue);
  return roundedValue;
};

export function extractCategoryIdFromDescription(description) {
  if (!description) return null;

  const match = description.match(/cid=(\d+)/);
  if (!match || !match[1]) return null;

  return parseInt(match[1]);
}

export const roundDecimalsInLabel = (label) => {
  if (!label) return "";

  const v = String(label).trim();

  // Handle ranges with special dash characters: 37.5–40.2 -> 38–40
  const range = v.match(
    /^\s*(\d+(?:\.\d+)?)\s*([-–—‑−])\s*(\d+(?:\.\d+)?)\s*$/
  );
  if (range) {
    const left = Math.round(Number(range[1]));
    const right = Math.round(Number(range[3]));
    return `${left}${range[2]}${right}`;
  }

  // Handle single numbers: 37.5 -> 38
  if (/^\d+(?:\.\d+)$/.test(v)) {
    return String(Math.round(Number(v)));
  }

  // Round any decimal number anywhere in the string: 37.5 -> 38
  return v.replace(/\b\d+\.\d+\b/g, (m) => String(Math.round(Number(m))));
};
